// src/components/chat/ChatHeader.jsx
import React, { useState, useEffect } from 'react';
import ChatAvatar from '../../components/ChatAvatar';
import GroupMembersDropdown from './GroupMembersDropdown';
import { formatLastSeen } from '../../utils/formatters';
import './ChatHeader.css';

const ChatHeader = ({
    currentChat,
    user,
    token,
    api,
    isConnecting,
    inCall,
    onStartCall,          // (isVideo) => void
    onToggleMobileMenu,
    onOpenEditGroup,      // (chat) => void
    onOpenInvite,         // (chat) => void
}) => {
    const [showMembers, setShowMembers] = useState(false);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    // Закрытие списка при клике вне
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showMembers && !e.target.closest('.members-dropdown') && !e.target.closest('.members-toggle')) {
                setShowMembers(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMembers]);

    // Сброс при смене чата
    useEffect(() => {
        setShowMembers(false);
        setMembers([]);
    }, [currentChat?.chat_id]);

    const loadMembers = async () => {
        if (!currentChat?.chat_id?.startsWith('group_')) return;
        
        setLoadingMembers(true);
        try {
            const groupId = currentChat.chat_id.replace('group_', '');
            const response = await api.get(`/groups/members?chat_id=${groupId}`);
            setMembers(response.data || []);
        } catch (error) {
            console.error('Failed to load members:', error);
            setMembers([]);
        } finally {
            setLoadingMembers(false);
        }
    };

    const toggleMembers = (e) => {
        e.stopPropagation();
        if (!showMembers && members.length === 0) {
            loadMembers();
        }
        setShowMembers(!showMembers);
    };

    if (!currentChat) return null;

    const isPrivate = currentChat.type === 'private';
    const isGroup = currentChat.type === 'group';
    const isCreator = currentChat.created_by === user?.id;

    return (
        <div className="chat-header">
            <div className="chat-header-left">
                <button
                    className="mobile-menu-btn"
                    onClick={onToggleMobileMenu}
                >
                    <i className="bi bi-chevron-left"></i>
                </button>

                <ChatAvatar
                    chat={currentChat}
                    token={token}
                    className="chat-avatar-header"
                />

                <div className="chat-info">
                    <div className="chat-title">{currentChat.title}</div>

                    {/* Статус для приватного чата */}
                    {isPrivate && (
                        <div className="chat-status">
                            {currentChat.is_typing ? (
                                <span className="typing-status">✏️ Печатает...</span>
                            ) : currentChat.is_online ? (
                                <span className="online-status">🟢 В сети</span>
                            ) : (
                                <span className="offline-status">
                                    {formatLastSeen(currentChat.last_seen)}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Участники для группы */}
                    {isGroup && (
                        <div className="chat-status">
                            <span 
                                className="online-status members-toggle"
                                onClick={toggleMembers}
                            >
                                <i className="bi bi-people-fill"></i> {currentChat.total_members || 1} участников
                                <i className={`bi bi-chevron-${showMembers ? 'up' : 'down'}`}></i>
                            </span>
                        </div>
                    )}
                </div>

                {/* Кнопки для приватного чата */}
                {isPrivate && (
                    <div className="chat-actions">
                        <button
                            onClick={() => onStartCall(currentChat, false)}
                            className="call-btn"
                            disabled={isConnecting || inCall}
                            title="Аудиозвонок"
                        >
                            <i className="bi bi-telephone"></i>
                        </button>
                        <button
                            onClick={() => onStartCall(currentChat, true)}
                            className="video-call-btn"
                            disabled={isConnecting || inCall}
                            title="Видеозвонок"
                        >
                            <i className="bi bi-camera-video"></i>
                        </button>
                    </div>
                )}

                {/* Кнопки для группы */}
                {isGroup && (
                    <div className="chat-actions">
                        <button
                            onClick={() => onOpenInvite(currentChat)}
                            className="call-btn"
                            title="Пригласить в группу"
                        >
                            <i className="bi bi-person-plus"></i>
                        </button>
                        {isCreator && (
                            <button
                                onClick={() => onOpenEditGroup(currentChat)}
                                className="call-btn"
                                title="Редактировать группу"
                            >
                                <i className="bi bi-pencil"></i>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Выпадающий список участников */}
            {showMembers && (
                <GroupMembersDropdown
                    members={members}
                    loading={loadingMembers}
                    user={user}
                    token={token}
                />
            )}
        </div>
    );
};

export default ChatHeader;